import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid2,
  Container,
  Typography,
  Paper,
  debounce,
  Autocomplete,
  MenuItem,
  Select,
  IconButton,
  SelectChangeEvent,
  Divider,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Course } from "../../model/entity/Course";
import {
  CourseValidationObj,
  courseValidationObjInit,
  validateCourse,
} from "../../model/validation/CourseValidation";
import handleAxiosError from "../../components/ErrorHandler";
import { Coach } from "../../model/entity/Coach";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Loading } from "../../components/Loading";
import { DayOfWeek, getDayIndex } from "../../model/common/DayOfWeek";
import {
  AddBoxOutlined,
  Delete,
  Forward,
  IndeterminateCheckBoxOutlined,
  ThumbDown,
  ThumbUp,
} from "@mui/icons-material";
import {
  generateTimeSlotOptions,
  notifErr,
  notifOk,
} from "../../components/Functions";
import { TrainingArea } from "../../model/entity/TrainingArea";
import { TimeSlotDTO, timeSlotDTOInit } from "../../model/entity/TimeSlotDTO";
import { TimeSlotAtTrainingAreaDTO } from "../../model/entity/TimeSlotAtTrainingAreaDTO";
import { CoachLeadsCourseDTO } from "../../model/entity/CoachLeadsCourseDTO";
import { Member } from "../../model/entity/Member";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import {
  deleteFetcher,
  getFetcher,
  putFetcher,
} from "../../components/Fetchers";
import { MemberInCourseDTO } from "../../model/entity/MemberInCourseDTO";

const EditCourse = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [trainingAreas, setTrainingAreas] = useState<TrainingArea[] | null>(
    null
  );
  const [coaches, setCoaches] = useState<Coach[] | null>(null);
  const [members, setMembers] = useState<Member[] | null>(null);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [memberInputVal, setMemberInputVal] = useState("");
  const [course, setCourse] = useState<Course | null>(null);
  const [courseErr, setCourseErr] = useState<CourseValidationObj>(
    courseValidationObjInit
  );

  useEffect(() => {
    const fetchData = async (url: string, callbackFn: any) => {
      try {
        const response = await getFetcher(url);
        callbackFn(response.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };

    fetchData(
      `${process.env.REACT_APP_API_URL}/courses/${id}`,
      (data: Course) => {
        const sortedTimeSlots = data.timeSlots.sort(sortTimeSlots);
        const sortedMemberInCourses =
          data.memberInCourses.sort(sortMemberInCourses);
        const sortedCoachLeadsCourses = data.coachLeadsCourses.sort(
          sortCoachLeadsCourses
        );
        setCourse({
          ...data,
          timeSlots: sortedTimeSlots,
          memberInCourses: sortedMemberInCourses,
          coachLeadsCourses: sortedCoachLeadsCourses,
        });
      }
    );
    fetchData(`${process.env.REACT_APP_API_URL}/coaches`, (data: Coach[]) => {
      const sortedData = data.sort((a: Coach, b: Coach) => {
        const res = a.firstName.localeCompare(b.firstName);
        return res ? res : a.lastName.localeCompare(b.lastName);
      });
      setCoaches(sortedData);
    });
    fetchData(`${process.env.REACT_APP_API_URL}/members`, (data: Member[]) => {
      const sortedData = data.sort((a: Member, b: Member) => {
        const res = a.firstName.localeCompare(b.firstName);
        return res ? res : a.lastName.localeCompare(b.lastName);
      });
      setMembers(sortedData);
    });
    fetchData(
      `${process.env.REACT_APP_API_URL}/training-areas/sorted-by-code`,
      setTrainingAreas
    );
  }, [id]);

  useEffect(() => {
    if (!course) return;
    if (triedSubmit) setCourseErr(validateCourse(course));
  }, [course, triedSubmit]);

  const sortTimeSlots = (a: TimeSlotDTO, b: TimeSlotDTO) => {
    const res = getDayIndex(a.day) - getDayIndex(b.day);
    return res ? res : a.startTime.localeCompare(b.startTime);
  };

  const sortMemberInCourses = (a: MemberInCourseDTO, b: MemberInCourseDTO) => {
    const res = a.member.firstName.localeCompare(b.member.firstName);
    return res ? res : a.member.lastName.localeCompare(b.member.lastName);
  };

  const sortCoachLeadsCourses = (
    a: CoachLeadsCourseDTO,
    b: CoachLeadsCourseDTO
  ) => {
    const res = a.coach.firstName.localeCompare(b.coach.firstName);
    return res ? res : a.coach.lastName.localeCompare(b.coach.lastName);
  };

  const handleCourseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!course) return;

    if (e.target.name === "color") {
      debouncedColorChange(e.target.value);
    } else {
      setCourse({ ...course, [e.target.name]: e.target.value });
    }
  };

  const handleAddTimeSlotRow = () => {
    if (!course) return;

    const updatedTimeSlots = [...course.timeSlots, timeSlotDTOInit];
    setCourse({ ...course, timeSlots: updatedTimeSlots });
  };

  const handleRemoveTimeSlotRow = (index: number) => {
    if (!course) return;

    const updatedTimeSlots = course.timeSlots.filter((ts, i) => i !== index);
    setCourse({ ...course, timeSlots: updatedTimeSlots });
  };

  const handleTimeSlotInputChange = (e: SelectChangeEvent, index: number) => {
    if (!course) return;

    const currentTimeSlot = course.timeSlots.at(index);
    if (!currentTimeSlot) return;

    let updatedTimeSlot: TimeSlotDTO;

    if (e.target.name === "day") {
      // considered as a new TimeSlot (reset course instances thus their substitutions)
      const updatedArr = currentTimeSlot.timeSlotAtTrainingAreas.map((ta) => {
        ta.id = null;
        return ta;
      });
      updatedTimeSlot = {
        ...currentTimeSlot,
        id: null,
        day: e.target.value,
        timeSlotAtTrainingAreas: updatedArr,
      };
    } else {
      updatedTimeSlot = { ...currentTimeSlot, [e.target.name]: e.target.value };
    }

    const updatedTimeSlots = course.timeSlots.map((ts, i) =>
      i === index ? updatedTimeSlot : ts
    );
    setCourse({ ...course, timeSlots: updatedTimeSlots });
  };

  const handleCoachChange = (newValues: Coach[]) => {
    if (!course) return;

    const oldValues = course.coachLeadsCourses.map((clc) => clc.coach);

    const addedItem = newValues.find(
      (val) => !oldValues.map((oldVal) => oldVal.id).includes(val.id)
    );
    const removedItem = oldValues.find(
      (val) => !newValues.map((newVal) => newVal.id).includes(val.id)
    );

    if (addedItem) {
      const updatedArr: CoachLeadsCourseDTO[] = [
        ...course.coachLeadsCourses,
        {
          id: null,
          coach: addedItem,
        },
      ];
      setCourse({ ...course, coachLeadsCourses: updatedArr });
    }

    if (removedItem) {
      const updatedArr: CoachLeadsCourseDTO[] = course.coachLeadsCourses.filter(
        (clc) => clc.coach.id !== removedItem.id
      );
      setCourse({ ...course, coachLeadsCourses: updatedArr });
    }
  };

  const handleAddMember = (member: Member | null) => {
    if (!course || !member) return;

    const newMemberInCourse = {
      id: null,
      member: member,
      paid: false,
    };

    const updatedArr = [...course.memberInCourses, newMemberInCourse];

    setCourse({ ...course, memberInCourses: updatedArr });
    setMemberInputVal("");
    setSelectedMember(null);
  };

  const handleMemberPaidChange = (index: number) => {
    if (!course) return;

    const currentItem = course?.memberInCourses.at(index);
    if (!currentItem) return;

    const updatedItem = { ...currentItem, paid: !currentItem.paid };

    const updatedArr = course.memberInCourses.map((mic, i) =>
      i === index ? updatedItem : mic
    );

    setCourse({ ...course, memberInCourses: updatedArr });
  };

  const handleRemoveMember = (index: number) => {
    if (!course) return;

    const updatedArr = course.memberInCourses.filter((mic, i) => i !== index);

    setCourse({ ...course, memberInCourses: updatedArr });
  };

  const handleTrainingAreaChange = (e: any, tsIndex: number) => {
    if (!course) return;

    const { value } = e.target;
    const newValues = value as number[];

    const currentTimeSlot = course.timeSlots.at(tsIndex);
    if (!currentTimeSlot) {
      notifErr("Nastala neočekávaná chyba.");
      return;
    }
    const oldValues = currentTimeSlot.timeSlotAtTrainingAreas.map(
      (ta) => ta.trainingArea.id!
    );

    const addedItem = newValues.find((val) => !oldValues.includes(val));
    const removedItem = oldValues.find((val) => !newValues.includes(val));

    if (addedItem) {
      const trainingArea = trainingAreas?.find((ta) => ta.id === addedItem);
      if (!trainingArea) {
        notifErr("Nastala neočekávaná chyba.");
        return;
      }
      const updatedArr: TimeSlotAtTrainingAreaDTO[] = [
        ...currentTimeSlot.timeSlotAtTrainingAreas,
        {
          id: null,
          trainingArea: trainingArea,
        },
      ];
      const updatedTimeSlot: TimeSlotDTO = {
        ...currentTimeSlot,
        timeSlotAtTrainingAreas: updatedArr,
      };
      const updatedTimeSlots: TimeSlotDTO[] = course.timeSlots.map(
        (ts, index) => (index !== tsIndex ? ts : updatedTimeSlot)
      );
      setCourse({ ...course, timeSlots: updatedTimeSlots });
    }

    if (removedItem) {
      const updatedArr: TimeSlotAtTrainingAreaDTO[] =
        currentTimeSlot.timeSlotAtTrainingAreas.filter(
          (ta) => ta.trainingArea.id !== removedItem
        );
      const updatedTimeSlot: TimeSlotDTO = {
        ...currentTimeSlot,
        timeSlotAtTrainingAreas: updatedArr,
      };
      const updatedTimeSlots: TimeSlotDTO[] = course.timeSlots.map(
        (ts, index) => (index !== tsIndex ? ts : updatedTimeSlot)
      );
      setCourse({ ...course, timeSlots: updatedTimeSlots });
    }
  };

  const debouncedColorChange = debounce((value: string) => {
    if (!course) return;

    setCourse((prev) => {
      if (prev === null) return prev;
      return { ...prev, color: value };
    });
  }, 5);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!course) return;

    setTriedSubmit(true);

    const courseErrors = validateCourse(course);
    setCourseErr(courseErrors);

    if (Object.values(courseErrors).some((val) => val === false)) {
      if (!courseErrors.timeSlots) {
        notifErr("Zkontrolujte údaje v časových slotech.");
      }
      return;
    }

    try {
      await putFetcher(
        `${process.env.REACT_APP_API_URL}/courses/${course.id}`,
        course
      );
      notifOk("Změny byly uloženy.");
      navigate(-1);
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleDeleteConfirm = async () => {
    if (!course) {
      notifErr("Nastala neočekávaná chyba.");
      return;
    }
    try {
      await deleteFetcher(
        `${process.env.REACT_APP_API_URL}/courses/${course?.id}`
      );
      notifOk("Kurz úspěšně smazán.");
      navigate(-1);
    } catch (err) {
      notifErr(handleAxiosError(err));
    } finally {
      setOpenDialog(false);
    }
  };

  const timeOptions = generateTimeSlotOptions(0, 24, true);

  if (
    course === null ||
    coaches === null ||
    trainingAreas === null ||
    members == null
  )
    return <Loading />;

  const dialogMsg = (
    <div>
      Opravdu chcete odebrat kurz <b>{course.name}</b>?<br />
      Tato akce je nevratná.
    </div>
  );

  return (
    <Container
      maxWidth="xl"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pb: "2rem",
      }}
    >
      <ConfirmationDialog
        open={openDialog}
        setOpen={setOpenDialog}
        msg={dialogMsg}
        handleSubmit={handleDeleteConfirm}
      />
      <Paper elevation={3} sx={{ p: "1rem" }}>
        <Typography variant="h5" sx={{ marginBottom: "1.5rem" }}>
          {course.name}
        </Typography>
        <Grid2 container sx={{ alignItems: "flex-start" }}>
          <Grid2
            container
            spacing={3}
            size={{ xs: 12, md: 6 }}
            sx={{ mb: "1rem" }}
          >
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Název kurzu"
                required
                name="name"
                value={course.name}
                error={!courseErr.name}
                onChange={handleCourseInputChange}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Autocomplete
                size="small"
                multiple
                disableClearable
                disableCloseOnSelect
                limitTags={1}
                value={course.coachLeadsCourses.map((clc) => clc.coach)}
                options={coaches.filter(
                  (c) =>
                    !course.coachLeadsCourses.some(
                      (clc) => clc.coach.id === c.id
                    )
                )}
                getOptionKey={(option) => option.id!}
                getOptionLabel={(option) =>
                  option.firstName + " " + option.lastName
                }
                filterSelectedOptions={true}
                filterOptions={(o, { inputValue }) =>
                  o.filter((option) =>
                    (option.firstName + " " + option.lastName)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Trenéři"
                    placeholder="Hledat..."
                  />
                )}
                onChange={(e, val) => handleCoachChange(val)}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Kapacita"
                type="number"
                required
                name="capacity"
                value={course.capacity}
                error={!courseErr.capacity}
                onChange={handleCourseInputChange}
                slotProps={{
                  input: {
                    onFocus: (event) => {
                      event.target.select();
                    },
                  },
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Členské příspěvky (Kč)"
                type="number"
                required
                name="fee"
                value={course.fee}
                error={!courseErr.fee}
                onChange={handleCourseInputChange}
                slotProps={{
                  input: {
                    onFocus: (event) => {
                      event.target.select();
                    },
                  },
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Popisek"
                multiline
                rows={4}
                name="description"
                value={course.description}
                error={!courseErr.description}
                onChange={handleCourseInputChange}
              />
            </Grid2>
            <Grid2
              size={{ xs: 12, md: 4 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "left",
              }}
            >
              <Grid2
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1rem",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Barva"
                  type="color"
                  required
                  name="color"
                  value={course.color}
                  error={!courseErr.color}
                  onChange={handleCourseInputChange}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Barva hex"
                  required
                  name="color"
                  value={course.color}
                  error={!courseErr.color}
                  onChange={handleCourseInputChange}
                />
              </Grid2>
              <FormControlLabel
                control={<Checkbox />}
                checked={course.competitive}
                label="Závodní skupina"
                onChange={() =>
                  setCourse({ ...course, competitive: !course.competitive })
                }
                sx={{ marginBottom: ".75rem" }}
              />
            </Grid2>

            {/* START TIMESLOTS PART */}

            <Grid2
              container
              sx={{
                width: "100%",
                height: "24.5rem",
                overflow: "auto",
                scrollbarWidth: "thin",
                display: "block",
              }}
            >
              <Grid2
                container
                size={{ xs: 12 }}
                sx={{
                  mb: "1rem",
                  position: "sticky",
                  top: 0,
                  background: "white",
                  zIndex: 100,
                }}
              >
                <Grid2 size={{ xs: 0.7 }}>
                  <IconButton
                    sx={{ padding: 0, color: "#0f0f0f" }}
                    onClick={handleAddTimeSlotRow}
                  >
                    <AddBoxOutlined />
                  </IconButton>
                </Grid2>

                <Grid2 size={{ xs: 3 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Den
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 1.7 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Od
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 1.7 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Do
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 3 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Sportoviště
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12 }} sx={{ pb: 1 }}>
                  <Divider
                    sx={{
                      width: "84%",
                    }}
                  />
                </Grid2>
              </Grid2>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {course.timeSlots.map((timeslot, index) => (
                  <Grid2
                    size={{ xs: 12 }}
                    container
                    spacing={2}
                    key={index}
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <Grid2 size={{ xs: 0.7 }}>
                      <IconButton
                        sx={{ padding: 0, color: "#0f0f0f" }}
                        onClick={() => handleRemoveTimeSlotRow(index)}
                      >
                        <IndeterminateCheckBoxOutlined />
                      </IconButton>
                    </Grid2>

                    <Grid2 size={{ xs: 3 }}>
                      <Select
                        size="small"
                        fullWidth
                        name="day"
                        value={timeslot.day}
                        onChange={(e) => handleTimeSlotInputChange(e, index)}
                        sx={{
                          maxHeight: "2.2rem",
                        }}
                      >
                        {Object.entries(DayOfWeek).map(([key, value]) => (
                          <MenuItem key={key} value={key}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid2>

                    <Grid2 size={{ xs: 1.7 }}>
                      <Select
                        fullWidth
                        name="startTime"
                        value={timeslot.startTime.slice(0, 5)}
                        onChange={(e) => handleTimeSlotInputChange(e, index)}
                        sx={{
                          maxHeight: "2.2rem",
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: "15rem",
                              overflowY: "auto",
                            },
                          },
                        }}
                      >
                        {timeOptions.map((time, index) => (
                          <MenuItem key={index} value={time}>
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid2>

                    <Grid2 size={{ xs: 1.7 }}>
                      <Select
                        fullWidth
                        name="endTime"
                        value={timeslot.endTime.slice(0, 5)}
                        onChange={(e) => handleTimeSlotInputChange(e, index)}
                        sx={{
                          maxHeight: "2.2rem",
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: "15rem",
                              overflowY: "auto",
                            },
                          },
                        }}
                      >
                        {timeOptions.map((time, index) => (
                          <MenuItem key={index} value={time}>
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid2>

                    <Grid2 size={{ xs: 3 }}>
                      <Select
                        multiple
                        size="small"
                        fullWidth
                        value={
                          course.timeSlots
                            .at(index)
                            ?.timeSlotAtTrainingAreas.map(
                              (ta) => ta.trainingArea.id
                            ) ?? []
                        }
                        onChange={(e) => handleTrainingAreaChange(e, index)}
                        sx={{
                          maxHeight: "2.2rem",
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: "15rem",
                              overflowY: "auto",
                            },
                          },
                        }}
                      >
                        {trainingAreas.map((area) => (
                          <MenuItem key={area.id!} value={area.id!}>
                            {area.code}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid2>
                  </Grid2>
                ))}
              </LocalizationProvider>
            </Grid2>

            {/* END TIMESLOTS PART */}
          </Grid2>

          {/* START MEMBERS PART */}

          <Grid2
            container
            size={{ xs: 12, md: 6 }}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Typography fontWeight="bold" variant="subtitle1">
              Přihlášení členové
            </Typography>

            <Autocomplete
              size="small"
              disableCloseOnSelect={true}
              value={selectedMember}
              inputValue={memberInputVal}
              sx={{ width: "30%", my: "0.5rem" }}
              options={members.filter(
                (m) =>
                  !course.memberInCourses.some((mic) => mic.member.id === m.id)
              )}
              getOptionKey={(member) => member.id!}
              getOptionLabel={(member) =>
                member.firstName + " " + member.lastName
              }
              filterOptions={(o, { inputValue }) =>
                o.filter((member) =>
                  (member.firstName + " " + member.lastName)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Přihlásit člena"
                  placeholder="Hledat..."
                />
              )}
              onChange={(e, val) => handleAddMember(val)}
              onInputChange={(e, val) => setMemberInputVal(val)}
            />

            <Table
              size="small"
              sx={{
                width: "80%",
                height: "35.5rem",
                overflowY: "auto",
                scrollbarWidth: "thin",
                display: "block",
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 100,
                  }}
                >
                  <TableCell sx={{ fontWeight: "bold", width: "35%" }}>
                    Jméno
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "center",
                      width: "10%",
                    }}
                  >
                    Zaplaceno
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "center",
                      width: "20%",
                    }}
                  >
                    Přejít
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "right",
                      width: "5%",
                    }}
                  >
                    Odebrat
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {course.memberInCourses.map((mic, index) => (
                  <TableRow key={index} className="">
                    <TableCell className="tableCell tableFirstCell">
                      {mic.member.firstName + " " + mic.member.lastName}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMemberPaidChange(index)}
                      >
                        {mic.paid ? (
                          <ThumbUp className="thumbUp" />
                        ) : (
                          <ThumbDown className="thumbDown" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/overview/members/edit/${mic.member.id!}`)
                        }
                      >
                        <Forward sx={{ color: "var(--gradientStart)" }} />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleRemoveMember(index)}
                      >
                        <Delete sx={{ color: "var(--orangeWarning)" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid2>
          {/* END MEMBERS PART */}
        </Grid2>

        <Grid2
          container
          size={{ xs: 12 }}
          sx={{ justifyContent: "space-between", mt: "2rem" }}
        >
          <Button
            variant="contained"
            className="deleteBtn"
            onClick={() => setOpenDialog(true)}
          >
            Smazat
          </Button>
          <div>
            <Button
              variant="outlined"
              className="cancelBtn"
              onClick={handleCancel}
              sx={{ mr: 1.5 }}
            >
              Zrušit
            </Button>
            <Button
              variant="outlined"
              className="submitBtn"
              onClick={handleSubmit}
            >
              Uložit
            </Button>
          </div>
        </Grid2>
      </Paper>
    </Container>
  );
};

export default EditCourse;
